import React, { useEffect, useState, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameMonth, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ModalEvento from '../components/ui/ModalEvento';
import ShareButton from '../components/ui/ShareButton';
import toast from 'react-hot-toast';
import './Calendario.css';

export default function Calendario() {
  const { logueado, esMaestro, esAdmin, usuario } = useAuth();
  const [fecha, setFecha]         = useState(new Date());
  const [eventos, setEventos]     = useState([]);
  const [selEvento, setSelEvento] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading]     = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    api.get(`/eventos?mes=${fecha.getMonth()+1}&anio=${fecha.getFullYear()}`)
      .then(r => setEventos(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [fecha]);

  useEffect(() => { 
    cargar();
    const t = setInterval(cargar, 30000); // 30s
    return () => clearInterval(t);
  }, [cargar]);

  const diasMes = eachDayOfInterval({ start: startOfMonth(fecha), end: endOfMonth(fecha) });
  const offset  = getDay(startOfMonth(fecha));

  const eventosDia = (dia) =>
    eventos.filter(ev => format(new Date(ev.fecha), 'yyyy-MM-dd') === format(dia, 'yyyy-MM-dd'));

  const colorEv = (est) => est === 'verde' ? 'ev-g' : est === 'amarillo' ? 'ev-y' : 'ev-r';

  const togglerParticipacion = async () => {
    if (!logueado) return toast.error('Inicia sesión para participar');
    
    const yaInscrito = selEvento.voluntariosActuales?.some(v => v._id === usuario?._id || v === usuario?._id);
    
    try {
      if (yaInscrito) {
        await api.delete(`/eventos/${selEvento._id}/salir`);
        toast.success('Participación cancelada');
      } else {
        if (selEvento.voluntariosActuales?.length >= selEvento.voluntariosNecesarios) {
          return toast.error('Cupo agotado');
        }
        await api.post(`/eventos/${selEvento._id}/unirse`);
        toast.success('¡Te has inscrito con éxito! 👋');
      }
      cargar();
      setSelEvento(null); // Cerrar para refrescar
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error');
    }
  };

  const donar = async () => {
    if (!logueado) { toast.error('Inicia sesión para donar'); return; }
    const monto = parseInt(prompt('¿Cuánto quieres donar? ($)')) || 0;
    if (monto < 1) return;
    try {
      const r = await api.post(`/eventos/${selEvento._id}/donar`, { monto });
      toast.success(`¡Donación de $${monto} registrada! +${r.data.puntos} pts 💚`);
      cargar();
    } catch (err) { toast.error(err.response?.data?.msg || 'Error'); }
  };

  const prevMes = () => setFecha(d => new Date(d.getFullYear(), d.getMonth()-1, 1));
  const nextMes = () => setFecha(d => new Date(d.getFullYear(), d.getMonth()+1, 1));

  const pct = (cur, max) => max > 0 ? Math.round((cur/max)*100) : 100;
  const colorBar = (est) => est==='verde' ? '#1a8a4a' : est==='amarillo' ? '#e8a020' : '#c0392b';

  const rangoHora = (ev) => {
    if (!ev?.horaInicio) return '';
    return ev.horaFin ? `${ev.horaInicio} – ${ev.horaFin}` : ev.horaInicio;
  };

  const yaParticipa = selEvento?.voluntariosActuales?.some(v => v._id === usuario?._id || v === usuario?._id);
  const estaLleno = selEvento?.voluntariosActuales?.length >= selEvento?.voluntariosNecesarios;

  return (
    <div className="page-wrap fade-up">
      <div className="cal-topbar">
        <div className="sec-title m-0">📅 Calendario de actividades</div>
        <div className="cal-nav">
          {(esAdmin || esMaestro) && <button className="btn btn-v btn-sm" onClick={() => setShowModal(true)}>+ Nueva actividad</button>}
          <button className="cal-arrow" onClick={prevMes}>←</button>
          <span className="cal-mes-label">{format(fecha,'MMMM yyyy',{locale:es})}</span>
          <button className="cal-arrow" onClick={nextMes}>→</button>
        </div>
      </div>

      <div className="cal-leyenda">
        <span className="leg"><span className="leg-dot bg-green-400"/>Completo</span>
        <span className="leg"><span className="leg-dot bg-amber-400"/>En progreso</span>
        <span className="leg"><span className="leg-dot bg-red-400"/>Faltan recursos</span>
      </div>

      {loading ? <div className="spinner"/> : (
        <div className="cal-grid">
          {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map(d => (
            <div key={d} className="cal-lbl">{d}</div>
          ))}
          {Array(offset).fill(null).map((_,i) => <div key={`e${i}`} />)}
          {diasMes.map(dia => {
            const evs = eventosDia(dia);
            return (
              <div
                key={dia.toISOString()}
                className={`cal-dia${isToday(dia)?' hoy':''}`}
                onClick={() => evs.length && setSelEvento(evs[0])}
              >
                <div className="dn">{format(dia,'d')}</div>
                {evs.map(ev => (
                  <div key={ev._id} className={`cal-ev ${colorEv(ev.estado)} relative`}>
                    <div className="text-[9px] flex justify-between mb-0.5">
                       <span>👥 {ev.voluntariosActuales?.length}/{ev.voluntariosNecesarios}</span>
                       {ev.modalidad === 'en_vivo' && <span className="badge-live">LIVE</span>}
                    </div>
                    {ev.horaInicio && (
                      <div className="text-[9px] font-bold opacity-80">🕐 {rangoHora(ev)}</div>
                    )}
                    <strong>{ev.nombre.substring(0,14)}{ev.nombre.length>14?'…':''}</strong>
                    {ev.esDePago && <div className="text-[9px] text-v font-bold">💰 Con Causa</div>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Detalle evento */}
      {selEvento && (
        <div className="ev-det fade-up">
          {selEvento.imagen && (
            <div className="ev-hero-img">
              <img src={selEvento.imagen} alt={selEvento.nombre} />
            </div>
          )}

          <div className="ev-det-header">
            <div>
              <div className="flex gap-2 mb-1">
                <span className={`badge ${selEvento.modalidad === 'en_vivo' ? 'badge-live-big' : 'badge-green'}`}>
                  {selEvento.modalidad === 'en_vivo' ? '🌐 ONLINE / EN VIVO' : '📍 PRESENCIAL'}
                </span>
                {selEvento.horaInicio && (
                  <span className="badge badge-blue">🕐 {rangoHora(selEvento)}</span>
                )}
                {selEvento.esDePago ? (
                  <span className="badge-gold">💰 COSTO: ${selEvento.precio}</span>
                ) : (
                  <span className="badge-green">✨ GRATIS</span>
                )}
              </div>
              <div className="ev-det-titulo">{selEvento.nombre}</div>
              <div className="text-sm text-txm font-semibold mt-1">{selEvento.descripcion}</div>
            </div>
            <button className="modal-close static text-[18px]" onClick={() => setSelEvento(null)}>✕</button>
          </div>

          <div className="grid-2 my-4">
            <div>
              <div className="text-xs text-txs font-bold mb-1">Voluntarios / Cupo</div>
              <div className="text-sm font-bold flex items-center gap-2">
                <span className="text-[18px]">👥</span> {selEvento.voluntariosActuales?.length || 0} / {selEvento.voluntariosNecesarios}
              </div>
              <div className="prog-wrap">
                <div className="prog-fill" style={{
                  width: pct(selEvento.voluntariosActuales?.length||0, selEvento.voluntariosNecesarios)+'%',
                  background: colorBar(selEvento.estado)
                }}/>
              </div>
            </div>
            {selEvento.esDePago && (
              <div className="bg-vc p-3 rounded-xl">
                <div className="text-xs text-vd font-bold mb-1">🎯 Propósito de los fondos:</div>
                <div className="text-sm italic text-txm">"{selEvento.propositoRecaudacion}"</div>
              </div>
            )}
          </div>

          {selEvento.modalidad === 'en_vivo' && selEvento.linkTransmision && (
             <div className="card mb-4 bg-blue-50 border border-blue-200 flex items-center gap-3">
                <span className="text-2xl">📹</span>
                <div className="flex-1">
                   <div className="text-xs font-bold text-blue-800">Acceso a la clase online:</div>
                   <a href={selEvento.linkTransmision} target="_blank" rel="noreferrer" className="text-sm text-blue-600 font-semibold break-all">
                      {selEvento.linkTransmision}
                   </a>
                </div>
             </div>
          )}

          <div className="ev-actions">
            <button 
              className={`btn ${yaParticipa ? 'btn-outline' : 'btn-v'}`} 
              onClick={togglerParticipacion}
              disabled={!yaParticipa && estaLleno}
            >
              {yaParticipa 
                ? '❌ Cancelar participación' 
                : estaLleno 
                  ? '🔒 Cupo lleno' 
                  : selEvento.esDePago 
                    ? `💳 Inscribirme ($${selEvento.precio})` 
                    : '👋 Unirme a la actividad'
              }
            </button>
            
            {selEvento.metaFondos > 0 && <button className="btn btn-d" onClick={donar}>💰 Donar a esta actividad</button>}

            {selEvento.publicId && selEvento.validado === true && (
              <ShareButton
                tipo={selEvento.estado === 'rojo' ? 'urgente' : 'actividad'}
                publicId={selEvento.publicId}
                title={`${selEvento.estado === 'rojo' ? '🚨 Urgente: ' : ''}${selEvento.nombre} — Red Cimarrona`}
                text={`Actividad de Red Cimarrona: ${selEvento.nombre}`}
                data={{
                  nombre: selEvento.nombre,
                  descripcion: selEvento.descripcion,
                  ubicacion: selEvento.ubicacion,
                  esUrgente: selEvento.estado === 'rojo'
                }}
              />
            )}

            {(esAdmin || esMaestro) && (
              <button className="btn btn-r btn-sm" onClick={async () => {
                if (!window.confirm('¿Eliminar esta actividad?')) return;
                await api.delete(`/eventos/${selEvento._id}`);
                toast.success('Actividad eliminada');
                setSelEvento(null); cargar();
              }}>Eliminar</button>
            )}
          </div>
        </div>
      )}

      {showModal && <ModalEvento onClose={() => setShowModal(false)} onCreado={cargar} />}
    </div>
  );
}
